import { Button } from '@amore/ui/components/button'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight, Plus } from 'lucide-react'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import { PromptList, taskPromptState, useAddPromptTask } from '@/patterns/prompts'
import { ScreenSection } from '@/patterns/screen'
import { useToast } from '@/shell/toaster'
import { byDueThenPriority } from '../tasks/model'
import { CompleteToggle, TaskTable, taskColumns } from '../tasks/task-table'

export function CeremonyPrompts() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const addTask = useAddPromptTask()
  const { showToast } = useToast()
  const candidates = [
    {
      code: 'ceremony-plan-b-decision',
      title: 'Kdo a kdy rozhodne o plánu B?',
      hint: 'Domluvte jednoho člověka a čas rozhodnutí, ať všichni vědí, podle čeho se řídit.',
      taskTitle: 'Domluvit rozhodnutí o plánu B obřadu',
      relevant: Boolean(
        data.ceremony.planB &&
          (!data.ceremony.planB.decisionByMemberId || !data.ceremony.planB.decisionTime),
      ),
    },
    {
      code: 'ceremony-rehearsal',
      title: 'Chcete si nástup projít předem?',
      hint: 'Krátká zkouška pomůže sladit příchody, pokyny i předání prstýnků.',
      taskTitle: 'Domluvit zkoušku obřadu',
      relevant: data.ceremony.steps.length > 0,
    },
  ]
  if (!canEdit) return null
  return (
    <PromptList
      title="Stojí za to ověřit"
      prompts={candidates.flatMap((prompt) => {
        const state = taskPromptState(
          data.tasks.filter((task) => task.context?.type === 'ceremony'),
          prompt.taskTitle,
        )
        if (!prompt.relevant || data.hiddenPrompts.includes(prompt.code) || state === 'done')
          return []
        return [
          {
            ...prompt,
            state,
            action: (
              <Button
                appearance="outline"
                tone="neutral"
                size="sm"
                onClick={() =>
                  addTask(prompt.taskTitle, { labels: [], context: { type: 'ceremony' } })
                }
              >
                <Plus className="size-3.5" aria-hidden="true" />
                Přidat úkol
              </Button>
            ),
          },
        ]
      })}
      onHide={(code) => {
        update((current) => ({ ...current, hiddenPrompts: [...current.hiddenPrompts, code] }))
        showToast({
          title: 'Doporučení skryto',
          action: {
            label: 'Vrátit zpět',
            altText: 'Znovu zobrazit doporučení k obřadu',
            onClick: () =>
              update((current) => ({
                ...current,
                hiddenPrompts: current.hiddenPrompts.filter((item) => item !== code),
              })),
          },
        })
      }}
    />
  )
}

export function CeremonyTasks() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const tasks = data.tasks
    .filter((task) => task.context?.type === 'ceremony')
    .sort((left, right) => Number(left.done) - Number(right.done) || byDueThenPriority(left, right))
  const navigate = useNavigate()
  const columns = taskColumns(data)
  return (
    <ScreenSection
      title="Co je potřeba zařídit"
      description="Úkoly k obřadu jsou součástí vašeho společného svatebního plánu."
      action={
        <Link
          to="/tasks"
          className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
        >
          Otevřít společné úkoly
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      }
    >
      <TaskTable
        tasks={tasks}
        grouped
        columns={columns}
        onOpen={(task) => void navigate({ to: `/tasks/${task.id}` })}
        rowAction={(task) => (
          <CompleteToggle
            task={task}
            canEdit={canEdit}
            onChange={(done) =>
              update((current) => ({
                ...current,
                tasks: replaceById(current.tasks, task.id, { done }),
              }))
            }
          />
        )}
        empty="Zatím žádné úkoly k obřadu. Až nějaký přidáte, najdete ho tady i ve společných úkolech."
      />
    </ScreenSection>
  )
}
